import React, { createContext, startTransition, useState, useEffect, useCallback } from "react";
import { doGet, setLogoutCallback } from "../utils/Apis";
import { identify, reset } from "../lib/analytics";
import i18n from "../i18n/i18n";
import { publicPaths } from "../rendering/publicRoutes";

// Align the active UI locale with the server-stored preference so a user
// who picked Italian on one device sees Italian on another. No-op when
// the field is missing or already matches — `changeLanguage` re-renders
// every translated component, so we only call it on a real diff.
const syncLanguageFromProfile = (profileUser) => {
  // Public URLs define their language; account preferences must not turn an
  // Italian URL into English content after the anonymous page is hydrated.
  if (typeof window !== "undefined" && publicPaths.includes(window.location.pathname)) return;
  const stored = profileUser?.preferredLanguage;
  if (!stored) return;
  const code = String(stored).slice(0, 2).toLowerCase();
  if (code && code !== (i18n.language || "").slice(0, 2)) {
    i18n.changeLanguage(code);
  }
};

// Build the PostHog identify traits from a /profile response. Splits durable
// person properties into $set (kept fresh on every login/refresh) and $set_once
// (acquisition-style values we record the first time we ever see this person).
//   - $name powers the nice person label in the PostHog UI.
//   - preferredLanguage is the durable backend language choice, distinct from
//     the live `locale` super/person prop that tracks what we actually render.
//   - plan/entitled mirror the backend entitlement the frontend already holds;
//     the backend stays the source of truth, this is a convenience mirror.
const identifyTraits = (profileUser, profile) => {
  const emailLower = profileUser.email.trim().toLowerCase();
  const displayName = profileUser.username || profileUser.displayName;
  return {
    $set: {
      email: emailLower,
      role: profileUser.role,
      username: profileUser.username,
      $name: displayName,
      preferredLanguage: profileUser.preferredLanguage,
      plan: profileUser.plan,
      entitled: profile?.entitlement?.entitled,
    },
    $set_once: {
      first_seen_at: new Date().toISOString(),
      initial_locale: (i18n.language || "").slice(0, 2) || undefined,
    },
  };
};

const AuthContext = createContext();

// Public auth-flow pages drive their own navigation after a /verify-account,
// /signup, /reset-password or /confirm-email-change POST. The background
// /profile probe + on-401 logout-and-bounce-home in this provider races
// with those page-owned redirects (stale localStorage tokens trigger 401,
// the refresh retry also 401s, then logoutCallback fires window.location =
// "/" right as the page is trying to navigate to Stripe Checkout). On these
// routes we skip both the probe and the bounce.
const AUTH_FLOW_ROUTES = [
  "/verify-account",
  "/signup",
  "/reset-password",
  "/confirm-email-change",
  // /tour hosts the pre-login verify step: a fresh signup can coexist with
  // stale tokens from an old session, and the bounce would rip the user off
  // the code-entry screen. The page fetches /profile itself when logged in.
  "/tour",
];

const isOnAuthFlowRoute = () =>
  typeof window !== "undefined" &&
  AUTH_FLOW_ROUTES.includes(window.location.pathname);

const getStoredUser = () => {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem("userToken");
    return raw ? JSON.parse(raw) : null;
  } catch {
    localStorage.removeItem("userToken");
    return null;
  }
};

export const AuthProvider = ({ children, initialAnonymous = false }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return !initialAnonymous && (getStoredUser()?.isLoggedIn || false);
  });
  const [isRoleAdmin, setIsRoleAdmin] = useState(() => {
    return !initialAnonymous && getStoredUser()?.role === "admin";
  });
  useEffect(() => {
    if (!initialAnonymous) return;
    const stored = getStoredUser();
    // As a transition, so the parts of the page whose script is still on its
    // way keep their built markup: React holds the update until it has
    // attached to them, where a plain update would have it throw their
    // markup away and draw them again once their script arrives.
    startTransition(() => {
      setIsLoggedIn(stored?.isLoggedIn || false);
      setIsRoleAdmin(stored?.role === "admin");
      setIsAuthResolved(true);
    });
  }, [initialAnonymous]);
  const [user, setUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [userError, setUserError] = useState(null);
  // A built public page starts anonymously so its HTML can be reused by all
  // visitors. Resolve the browser's saved session after attaching to it.
  const [isAuthResolved, setIsAuthResolved] = useState(!initialAnonymous);

  const logout = useCallback(() => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("user");
    setIsLoggedIn(false);
    setUser(null);
    setIsRoleAdmin(false);
    setUserError(null);
    reset();
  }, []);

  // Single /profile loader behind the session probe, login() and refreshUser.
  const loadProfile = useCallback(async () => {
    setIsLoadingUser(true);
    setUserError(null);
    try {
      const response = await doGet("/profile");
      if (response?.status === 200) {
        localStorage.setItem("user", JSON.stringify(response.data));
        setUser(response.data);
        // Update role from API response if different
        if (response.data?.user?.role) {
          setIsRoleAdmin(response.data.user.role === "admin");
        }
        const profileUser = response.data?.user;
        if (profileUser?.email) {
          const emailLower = profileUser.email.trim().toLowerCase();
          identify(emailLower, identifyTraits(profileUser, response.data));
        }
        syncLanguageFromProfile(profileUser);
      } else if (response?.status === 401 || response?.status === 403) {
        logout();
      } else {
        setUserError("Failed to load user profile. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUserError("Unable to load user profile. Please check your connection and try again.");
    } finally {
      setIsLoadingUser(false);
    }
  }, [logout]);

  // Set by login(): the tokens in storage were issued during this page
  // session, so they are fresh even on an auth-flow route.
  const [hasFreshLogin, setHasFreshLogin] = useState(false);

  // Load the profile whenever we're logged in without one. On auth-flow
  // routes only a session restored from storage is skipped (it may be stale,
  // see AUTH_FLOW_ROUTES); a login made on one of those pages must still load
  // it, or the user stays logged in with no profile until a full reload.
  useEffect(() => {
    if (!isLoggedIn || user) return;
    if (isOnAuthFlowRoute() && !hasFreshLogin) return;
    loadProfile();
  }, [isLoggedIn, user, hasFreshLogin, loadProfile]);

  // Update `isLoggedIn` state whenever localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const userToken = getStoredUser();
      setIsLoggedIn(userToken?.isLoggedIn || false);
      setIsRoleAdmin(userToken?.role === "admin");
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const [isLoginOpen, setLoginOpen] = useState(false);
  const [isForgetPasswordOpen, setIsForgetPasswordOpen] = useState(false);
  const [isForgetPasswordResetOpen, setIsForgetPasswordResetOpen] =
    useState(false);

  // Login.jsx writes the access/refresh tokens to localStorage before calling
  // this; we just flip state and the /profile effect above picks up `user`.
  const login = () => {
    setHasFreshLogin(true);
    setIsLoggedIn(true);
    setUser(null);
  };

  const refreshUser = async () => {
    if (!isLoggedIn) return;
    await loadProfile();
  };

  // Set up logout callback for token expiration handling
  useEffect(() => {
    const handleTokenExpiration = () => {
      logout();
      // On auth-flow routes the page itself drives navigation (e.g. to
      // Stripe Checkout); bouncing home here would override that and
      // strand the user signed-out on /.
      if (isOnAuthFlowRoute()) return;
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      }
    };

    setLogoutCallback(handleTokenExpiration);

    // Cleanup: remove callback when component unmounts
    return () => {
      setLogoutCallback(null);
    };
  }, [logout]);

  const openLogin = () => {
    setLoginOpen(true);
    closeForgetPassword();
  };
  const openForgetPasswordReset = () => {
    setIsForgetPasswordResetOpen(true);
    closeForgetPassword();
  };

  const closeLogin = () => setLoginOpen(false);

  const openForgetPassword = () => {
    setIsForgetPasswordOpen(true);
    closeLogin();
  };

  const closeForgetPassword = () => setIsForgetPasswordOpen(false);
  const closeForgetPasswordReset = () => setIsForgetPasswordResetOpen(false);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isAuthResolved,
        isRoleAdmin,
        user,
        // Computed entitlement from /profile — the single source the gate and
        // AccountSettings route on (state / next_action / entitled / days_left).
        entitlement: user?.entitlement || null,
        // Stripe recovery link minted by the backend on the login 200 (stored
        // in the userToken blob; absent from /profile). Lets the post-login
        // prompt send a non-entitled user straight to Stripe Checkout/portal.
        // Read fresh from storage so it reflects the latest login.
        recoveryUrl: isLoggedIn ? getStoredUser()?.recovery_url || null : null,
        isLoadingUser,
        userError,
        login,
        logout,
        refreshUser,
        isLoginOpen,
        openLogin,
        closeLogin,
        isForgetPasswordOpen,
        openForgetPassword,
        closeForgetPassword,
        isForgetPasswordResetOpen,
        openForgetPasswordReset,
        closeForgetPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
